/**
 * HubNavigator — Storytelling Deck Transit Orchestrator.
 * 
 * Coordinates diegetic navigation across the 3 operational decks of the
 * VOID Orbital Carrier:
 * - DECK 01 // CARGO BAY (Booster Pack Opening)
 * - DECK 02 // MOD MATRIX (Cyber Slot Machine)
 * - DECK 03 // ARSENAL HANGAR (Customization Showroom & Firing Range)
 * 
 * Features:
 * 1. Zero button fatigue ("einschmiegen statt anstrengender Schaltflächen")
 * 2. 0% mobile gesture conflict (top-docked HUD with >190px clearance from card swipe zone)
 * 3. Real-time telemetry sync for Fugazi ⬡, Spin Tickets 🎟️, and Booster Packs 📦
 * 4. Atomic deck switching with zero flickering back to the battlefield
 * 5. Clean teardown of pack reveal on deck transit to eliminate mobile VRAM/cache pressure
 */

import { wallet } from '../engine/wallet.js';
import { inventory } from '../engine/inventory.js';

export class HubNavigator {
  constructor({ packOpeningModal = null, slotMachineModal = null, showroomModal = null, deckBuilder = null, deckBuilderModal = null, audio = null, game = null } = {}) {
    this.packModal = packOpeningModal;
    this.slotModal = slotMachineModal;
    this.showroomModal = showroomModal;
    // Deck Selector (#deck-modal) / Deck Builder (#deckbuilder-modal) - now
    // fully integrated as operational DECK 01 // ARCHIVE on the transit rail.
    this.deckBuilder = deckBuilder;
    this.deckBuilderModal = deckBuilderModal;
    this.audio = audio;
    this.game = game;

    this.activeDeck = 'archive'; // 'archive' | 'cargo' | 'matrix' | 'arsenal'
    this._isTransitioning = false;

    this.init();
  }

  init() {
    if (typeof document === 'undefined') return;
    this.bindEvents();
    this.updateTelemetry();
    this.initModalObserver();

    wallet.addListener(() => this.updateTelemetry());
    inventory.addListener(() => this.updateTelemetry());
  }

  setModals({ packOpeningModal, slotMachineModal, showroomModal, deckBuilder, deckBuilderModal, audio, game }) {
    if (packOpeningModal) this.packModal = packOpeningModal;
    if (slotMachineModal) this.slotModal = slotMachineModal;
    if (showroomModal) this.showroomModal = showroomModal;
    if (deckBuilder) this.deckBuilder = deckBuilder;
    if (deckBuilderModal) this.deckBuilderModal = deckBuilderModal;
    if (audio) this.audio = audio;
    if (game) this.game = game;
  }

  initModalObserver() {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
    const observer = new MutationObserver(() => {
      this.updateFloatingVoidButton();
    });
    const modalIds = ['deck-modal', 'pack-opening-modal', 'slot-machine-modal', 'showroom-modal', 'deckbuilder-modal'];
    modalIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el, { attributes: true, attributeFilter: ['class'] });
      }
    });
    this.updateFloatingVoidButton();
  }

  bindEvents() {
    // Deck tabs across all station rails in DOM
    document.addEventListener('click', (e) => {
      const floatBtn = e.target.closest('#floating-back-to-void');
      if (floatBtn) {
        e.preventDefault();
        this.exitStation();
        return;
      }

      const tab = e.target.closest('.deck-tab');
      if (tab) {
        if (tab.dataset.action === 'exit') {
          e.preventDefault();
          this.exitStation();
          return;
        }
        const deck = tab.dataset.deck;
        if (deck) {
          e.preventDefault();
          this.navigateTo(deck);
          return;
        }
      }

      const hatch = e.target.closest('.btn-departure-hatch');
      if (hatch) {
        e.preventDefault();
        this.exitStation();
        return;
      }

      // Diegetic shortcut links on Station Telemetry Bar (Archive)
      // Fugazi -> Cargo Bay, Tickets -> Supply (Matrix), MMR -> Arsenal > Progress > Ranked
      const pill = e.target.closest('.station-telemetry-bar .telemetry-pill');
      if (pill) {
        e.preventDefault();
        if (pill.classList.contains('pill-fugazi')) {
          this.navigateTo('cargo');
        } else if (pill.classList.contains('pill-tickets')) {
          this.navigateTo('matrix');
        } else if (pill.classList.contains('pill-mmr')) {
          this.navigateTo('arsenal', { progressView: 'ladder' });
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const pill = e.target?.closest?.('.station-telemetry-bar .telemetry-pill');
      if (pill) {
        e.preventDefault();
        if (pill.classList.contains('pill-fugazi')) {
          this.navigateTo('cargo');
        } else if (pill.classList.contains('pill-tickets')) {
          this.navigateTo('matrix');
        } else if (pill.classList.contains('pill-mmr')) {
          this.navigateTo('arsenal', { progressView: 'ladder' });
        }
      }
    });
  }

  isGameRunning() {
    const g = this.game || (typeof window !== 'undefined' && window.tcgGame);
    if (!g || !g.isRunning || g.isGameOver) return false;
    // A match is only considered actively running if the player has played at least 1 card
    const myTeam = g.myTeam || g.battlefield?.playerTeam || 'western';
    const playerPlayed = g.battlefield?.stats?.teams?.[myTeam]?.cardsPlayed || 0;
    const campaignArmed = !!(g.isCampaignMission && g.campaignCombatArmed);
    return (playerPlayed >= 1 || campaignArmed);
  }

  isAnyModalOpen() {
    if (this.deckBuilder?.isOpen) return true;
    if (this.deckBuilderModal?.isOpen?.()) return true;
    if (this.packModal?.isOpen?.()) return true;
    if (this.slotModal?.modalEl && !this.slotModal.modalEl.classList.contains('hidden')) return true;
    if (this.showroomModal?.modalEl && !this.showroomModal.modalEl.classList.contains('hidden')) return true;
    if (typeof document !== 'undefined') {
      const active = document.querySelectorAll('#deck-modal.active, #pack-opening-modal:not(.hidden), #slot-machine-modal:not(.hidden), #showroom-modal:not(.hidden), #deckbuilder-modal:not(.hidden)');
      return active.length > 0;
    }
    return false;
  }

  updateFloatingVoidButton() {
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('floating-back-to-void');
    if (!btn) return;
    const shouldShow = this.isAnyModalOpen() && this.isGameRunning();
    btn.classList.toggle('hidden', !shouldShow);
  }

  updateTelemetry() {
    const fugazi = wallet.getFugazi();
    const tickets = wallet.getTickets();
    const packs = inventory.getBoosterPacks();

    document.querySelectorAll('.nav-fugazi-val').forEach(el => {
      el.textContent = fugazi.toLocaleString();
    });
    const teleFugazi = document.getElementById('telemetry-fugazi-val');
    if (teleFugazi) teleFugazi.textContent = fugazi.toLocaleString();

    document.querySelectorAll('.nav-tickets-val').forEach(el => {
      el.textContent = tickets.toLocaleString();
    });
    const teleTickets = document.getElementById('telemetry-tickets-val');
    if (teleTickets) teleTickets.textContent = tickets.toLocaleString();

    document.querySelectorAll('.nav-packs-val').forEach(el => {
      el.textContent = packs.toLocaleString();
    });
  }

  setActiveDeck(deckId) {
    this.activeDeck = deckId;
    document.querySelectorAll('.deck-tab').forEach(tab => {
      const isActive = tab.dataset.deck === deckId;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.updateFloatingVoidButton();
  }

  playTransitBeep(deckId) {
    try {
      const audio = this.audio || (typeof window !== 'undefined' && window.tcgGame?.audio);
      if (!audio || audio.isMuted) return;
      // Registry-first (cargo_bay.hub_navigation) so a file assigned in the
      // Sound Review tool replaces the synthesized tone live in-game; falls
      // through to the original oscillator beep when no file is assigned.
      if (typeof audio.playRegistrySound === 'function') {
        const res = audio.playRegistrySound('cargo_bay', 'hub_navigation', deckId);
        if (res !== null) return;
      }
      if (typeof audio.playTone === 'function') {
        const freqs = { archive: 550, cargo: 650, matrix: 850, arsenal: 1050, exit: 440 };
        audio.playTone(freqs[deckId] || 750, 0.06);
      }
    } catch (e) {
      // Audio fallback silent
    }
  }

  navigateTo(deckId, options = {}) {
    if (this._isTransitioning) return;
    this.playTransitBeep(deckId);

    // Jumping off from the online setup screen (its header reuses this rail):
    // drop any running search before leaving, then close it.
    const lobby = this.game?.onlineLobby;
    if (lobby?.isOpen) {
      lobby.cancelWaiting({ returnToArchive: false });
      lobby.closeModal();
    }

    const menuKeys = { archive: 'deckSelector', cargo: 'cargoBay', matrix: 'slotMachine', arsenal: 'showroom' };
    const targetKey = menuKeys[deckId];
    if (targetKey) this.game?.pauseForMenu?.(targetKey);

    this.setActiveDeck(deckId);

    if (deckId === 'archive') {
      if (this.packModal?.isOpen()) {
        this.packModal.close(); // Clean up pack reveal and release mobile memory
      }
      this.slotModal?.close();
      this.showroomModal?.close();
      const editedDeckId = options?.selectDeckKey || this.deckBuilderModal?.currentDeck?.id || this.deckBuilderModal?.lastEditedDeckId || this.game?.currentPlayerDeckId || null;
      if (this.deckBuilderModal?.isOpen?.()) this.deckBuilderModal.close();
      this.deckBuilder?.openModal({ isMenuSwitch: true, selectDeckKey: editedDeckId });
    } else if (deckId === 'cargo') {
      if (this.deckBuilder?.isOpen) this.deckBuilder.closeModal({ isMenuSwitch: true });
      if (this.deckBuilderModal?.isOpen?.()) this.deckBuilderModal.close();
      this.slotModal?.close();
      this.showroomModal?.close();
      this.packModal?.open();
    } else if (deckId === 'matrix') {
      if (this.deckBuilder?.isOpen) this.deckBuilder.closeModal({ isMenuSwitch: true });
      if (this.deckBuilderModal?.isOpen?.()) this.deckBuilderModal.close();
      if (this.packModal?.isOpen()) {
        this.packModal.close(); // Clean up pack reveal before loading slot machine
      }
      this.showroomModal?.close();
      this.slotModal?.open();
    } else if (deckId === 'arsenal') {
      if (this.deckBuilder?.isOpen) this.deckBuilder.closeModal({ isMenuSwitch: true });
      if (this.deckBuilderModal?.isOpen?.()) this.deckBuilderModal.close();
      if (this.packModal?.isOpen()) {
        this.packModal.close(); // Clean up pack reveal before loading showroom
      }
      this.slotModal?.close();
      this.showroomModal?.open(options);
    }

    this.updateTelemetry();
    this.updateFloatingVoidButton();
  }

  exitStation() {
    this.playTransitBeep('exit');
    if (this.deckBuilder?.isOpen) this.deckBuilder.closeModal();
    if (this.deckBuilderModal?.isOpen?.()) this.deckBuilderModal.close();
    this.packModal?.close();
    this.slotModal?.close();
    this.showroomModal?.close();
    this.updateFloatingVoidButton();
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      try {
        const s = window.history.state?.screen;
        if (s === 'hub' || s === 'archive' || s === 'archive_multiplayer') {
          window.history.replaceState({ screen: 'start' }, '');
        }
      } catch (e) {}
    }
    // No match is running (and none just ended): the hub was entered
    // straight from the Start Screen (Commander avatar -> Arsenal), so the
    // only sensible place to go back to is the Start Screen - otherwise
    // exiting would land on the idle, unstarted battlefield.
    const g = this.game;
    const postGameVisible = typeof document !== 'undefined' && !document.getElementById('post-game-return-overlay')?.classList?.contains('hidden');
    if (g && !g.isRunning && (!g.isGameOver || !postGameVisible)) g.startScreen?.show();
  }
}

export const hubNavigator = new HubNavigator();

